import { useState } from 'react';
import type { ProductTagImportPreview } from '../../app/api/agent';
import { useModalOpen, usePolarisEvents } from '../../app/components/polaris';
import { tagIssueExplanation } from './productTagImportUi';
import './ProductTagReviewModal.css';

type Props = {
    preview: ProductTagImportPreview;
    busy: boolean;
    error?: string;
    onClose: () => void;
    onApprove: () => void;
    onDiscard: () => void;
    initialConfirmDiscard?: boolean;
};

const pageSize = 5;

/** Mounted only while a preview is open, so the modal shows on mount. */
export default function ProductTagReviewModal({ preview, busy, error, onClose, onApprove, onDiscard, initialConfirmDiscard = false }: Props) {
    const [confirmDiscard, setConfirmDiscard] = useState(initialConfirmDiscard);
    const [page, setPage] = useState(0);
    const modalRef = useModalOpen(true, () => { if (!busy) onClose(); });
    const hasNextPage = (page + 1) * pageSize < preview.rows.length;
    const tableEvents = usePolarisEvents({
        nextpage: () => { if (hasNextPage) setPage(current => current + 1); },
        previouspage: () => setPage(current => Math.max(0, current - 1)),
    });
    const sampleRows = preview.rows.slice(page * pageSize, page * pageSize + pageSize);

    return (
        <s-modal ref={modalRef} id="pf-tag-review-modal" heading={confirmDiscard ? 'Discard this preview?' : 'Review your tag import'}>
            <s-stack gap="base">
                {error && <s-banner tone="critical">{error}</s-banner>}
                {confirmDiscard ? (
                    <s-paragraph>This removes the prepared preview without importing anything. Your existing fitments stay the same.</s-paragraph>
                ) : <>
                    <s-heading>{preview.isApplied ? 'Imported fitments' : preview.willApplyRows > 0 ? 'Ready to import' : 'No fitments are ready to import'}</s-heading>
                    <s-grid gridTemplateColumns="1fr 1fr" gap="base">
                        <s-stack gap="none">
                            <s-text color="subdued">{preview.isApplied ? 'Matches imported' : 'Matches ready to import'}</s-text>
                            <s-text fontWeight="semibold">{preview.willApplyRows.toLocaleString()}</s-text>
                        </s-stack>
                        <s-stack gap="none">
                            <s-text color="subdued">Products</s-text>
                            <s-text fontWeight="semibold">{preview.productCount.toLocaleString()}</s-text>
                        </s-stack>
                    </s-grid>
                    {preview.catalogOnly && <s-text color="subdued">Matching against your vehicle catalog only. Reference vehicle data is excluded.</s-text>}
                    {!!preview.diagnostics?.unresolvedTags && (
                        <s-banner tone="warning" heading={`${preview.diagnostics.unresolvedTags.toLocaleString()} tags couldn’t be matched`}>
                            <s-stack gap="small-200">
                                <s-paragraph>
                                    {preview.isApplied ? 'These tags were left out of the import.' : preview.willApplyRows > 0
                                        ? 'These tags won’t be imported. You can still import the fitments that are ready.'
                                        : 'Check these tags or adjust the tag format, then prepare a new preview.'}
                                </s-paragraph>
                                <details className="pf-tag-review-details">
                                    <summary>See why and how to fix them</summary>
                                    <ul>
                                        {preview.diagnostics.issues.map(issue => (
                                            <li key={issue.reason}>
                                                <p>{issue.count.toLocaleString()} tags: {tagIssueExplanation(issue.reason)}</p>
                                                <ul>{issue.examples.map((example, index) => <li key={`${example.productId}-${index}`}>{example.tag}</li>)}</ul>
                                            </li>
                                        ))}
                                    </ul>
                                    <p>Check the vehicle names in your catalog, or choose a tag format that separates the make, model and year. Then prepare a new preview.</p>
                                </details>
                            </s-stack>
                        </s-banner>
                    )}
                    {preview.skippedRows > 0 && (
                        <s-banner tone="info">
                            {preview.skippedRows.toLocaleString()} matches {preview.isApplied ? 'were' : 'will be'} left out because the vehicles aren’t in your catalog and this import uses existing vehicles only.
                        </s-banner>
                    )}
                    {preview.rows.length > 0 && (
                        <s-stack gap="small-200">
                            <s-grid gridTemplateColumns="1fr auto" gap="small-200" alignItems="center">
                                <s-heading>Example fitments</s-heading>
                                <s-text color="subdued">
                                    {`${page * pageSize + 1}–${Math.min(page * pageSize + pageSize, preview.rows.length)} of ${Math.max(preview.totalRows, preview.rows.length).toLocaleString()}`}
                                </s-text>
                            </s-grid>
                            <s-table ref={tableEvents} paginate={preview.rows.length > pageSize}
                                hasPreviousPage={page > 0} hasNextPage={hasNextPage}>
                                <s-table-header-row>
                                    <s-table-header listSlot="primary">Product</s-table-header>
                                    <s-table-header>Fits this vehicle</s-table-header>
                                </s-table-header-row>
                                <s-table-body>
                                    {sampleRows.map(row => (
                                        <s-table-row key={row.id}>
                                            <s-table-cell>
                                                <s-stack gap="none">
                                                    <s-text>{row.productTitle || `Product ${row.productId}`}</s-text>
                                                    {row.productSku && <s-text color="subdued">SKU: {row.productSku}</s-text>}
                                                    <s-text color="subdued">Tag: {row.sourceTag}</s-text>
                                                </s-stack>
                                            </s-table-cell>
                                            <s-table-cell>
                                                <s-stack gap="none">
                                                    <s-text>{row.isUniversal ? 'Universal product — fits any vehicle' : [row.year, row.make, row.model, row.submodel].filter(Boolean).join(' ')}</s-text>
                                                    {!row.willApply && <s-text color="subdued">Not included</s-text>}
                                                </s-stack>
                                            </s-table-cell>
                                        </s-table-row>
                                    ))}
                                </s-table-body>
                            </s-table>
                        </s-stack>
                    )}
                    {(preview.willCreateFitmentRecords > 0 || !!preview.diagnostics?.ignoredTags) && (
                        <details className="pf-tag-review-details">
                            <summary>Additional import details</summary>
                            {preview.willCreateFitmentRecords > 0 && <p>{preview.isApplied ? 'This preview included fitments that needed new vehicle records.' : 'Some fitments need new vehicle records. These will be created when you import.'}</p>}
                            {!!preview.diagnostics?.ignoredTags && <p>{preview.diagnostics.ignoredTags.toLocaleString()} non-vehicle tags were ignored.</p>}
                        </details>
                    )}
                </>}
            </s-stack>

            {preview.awaitingApproval && (confirmDiscard ? (
                <s-button slot="primary-action" variant="primary" tone="critical" loading={busy} onClick={onDiscard}>Discard preview</s-button>
            ) : (
                <s-button slot="primary-action" variant="primary" loading={busy} disabled={preview.willApplyRows === 0} onClick={onApprove}>
                    Import fitments
                </s-button>
            ))}
            {confirmDiscard ? (
                <s-button slot="secondary-actions" disabled={busy} onClick={() => setConfirmDiscard(false)}>Keep preview</s-button>
            ) : <>
                <s-button slot="secondary-actions" disabled={busy} onClick={onClose}>Close</s-button>
                {preview.awaitingApproval && (
                    <s-button slot="secondary-actions" tone="critical" disabled={busy} onClick={() => setConfirmDiscard(true)}>Discard preview</s-button>
                )}
            </>}
        </s-modal>
    );
}
